import type { ReactNode } from "react";

import { type BadgeProps, Icon, Text } from "@alpacahq/ui/old";

import { NewWindowLink } from "~/v2/components/common";
import type { PlanBoxProps } from "./PlanBox";

const CRYPTO_REGIONS_URL =
  "https://alpaca.markets/support/what-regions-support-cryptocurrency-trading";
const CRYPTO_TRADING_URL =
  "https://docs.alpaca.markets/docs/crypto-trading#crypto-fees";
export const ALPACA_ELITE_MARKETING_URL = "https://alpaca.markets/elite";

export const BADGE_VARIANT: Record<"basic" | "elite", BadgeProps["variant"]> = {
  basic: "gray",
  elite: "blue",
};

export const FEATURES: {
  title: ReactNode;
  items: {
    title: ReactNode;
    // [Basic, Elite]
    plans: [ReactNode, ReactNode];
  }[];
}[] = [
  {
    title: "Commissions",
    items: [
      // @todo: add link below
      {
        title: "US Exchange-listed stocks / ETFs",
        plans: [
          <Text key="basic">$0</Text>,
          <Text key="elite">All-in (fixed) or Cost Plus (tiered)</Text>,
        ],
      },
      {
        title: "US Exchange-listed Options",
        plans: [<Text key="basic">$0</Text>, <Text key="elite">$0</Text>],
      },

      {
        title: (
          <>
            Crypto{" "}
            <span className="font-normal text-xs">
              (only available to select regions,{" "}
              <NewWindowLink
                hasIcon={false}
                to={CRYPTO_REGIONS_URL}
                className="inline"
              >
                see here
              </NewWindowLink>
              )
            </span>
          </>
        ),
        plans: [
          <NewWindowLink key="basic" hasIcon={false} to={CRYPTO_TRADING_URL}>
            See more
          </NewWindowLink>,
          <NewWindowLink key="elite" hasIcon={false} to={CRYPTO_TRADING_URL}>
            See more
          </NewWindowLink>,
        ],
      },
    ],
  },
  {
    title: "Eligible Accounts",
    items: [
      {
        title: "Retail Investors",
        plans: [
          <Icon key="basic" name="Check" />,
          <Icon key="elite" name="Check" />,
        ],
      },
      {
        title: "Business Accounts",
        plans: [
          <Icon key="basic" name="Check" />,
          <Icon key="elite" name="Check" />,
        ],
      },
      {
        title: "Minimum Net Deposit",
        plans: [<Text key="basic">$0</Text>, <Text key="elite">$30,000</Text>],
      },
    ],
  },
  {
    title: "Account Features",
    items: [
      {
        title: "Paper Trading",
        plans: [
          <Icon key="basic" name="Check" />,
          <Icon key="elite" name="Check" />,
        ],
      },
      {
        title: "API Trading",
        plans: [
          <Icon key="basic" name="Check" />,
          <Icon key="elite" name="Check" />,
        ],
      },
      {
        title: "API Calls Limit",
        plans: [
          <Text key="basic">200 calls / minute</Text>,
          <Text key="elite">1000 calls / minute</Text>,
        ],
      },
      {
        title: "Extended Hours Trading",
        plans: [
          <Icon key="basic" name="Check" />,
          <Icon key="elite" name="Check" />,
        ],
      },
    ],
  },
  {
    title: "Premium Order Types",
    items: [
      {
        title: "MOO / LOO (Market Open / Limit Open)",
        plans: [
          <Icon key="basic" name="XMark" />,
          <Icon key="elite" name="Check" />,
        ],
      },
      {
        title: "MOC / LOC (Market Close / Limit Close)",
        plans: [
          <Icon key="basic" name="XMark" />,
          <Icon key="elite" name="Check" />,
        ],
      },
      {
        title: "IOC (Immediate or cancel)",
        plans: [
          <Icon key="basic" name="XMark" />,
          <Icon key="elite" name="Check" />,
        ],
      },
      {
        title: "VWAP, TWAP, Pegged, Midpoint",
        plans: [
          <Icon key="basic" name="XMark" />,
          <Icon key="elite" name="Check" />,
        ],
      },
    ],
  },
];

export const PLANS: {
  [key: string]: Omit<PlanBoxProps, "button">;
} = {
  basic: {
    tagline: "For Retail Investors",
    title: "Commission-Free",
    list: [
      "$0 commissions on US listed stocks / ETFs and options",
      "No minimum deposit required",
    ],
    className: "bg-gray-100",
  },
  elite: {
    tagline: "For Advanced Traders*",
    title: "Elite Smart Router",
    list: [
      "Advanced order execution with low costs",
      "Premium order types",
      "Low minimum deposit required",
    ],
    learnMore: ALPACA_ELITE_MARKETING_URL,
    className: "bg-black text-white",
  },
};

export const COMMISSION_MODELS: Record<
  string,
  { fixed: string; tiered: string }
> = {
  "Up to 200,000": { fixed: "$0.0040", tiered: "$0.0025" },
  "200,001 to 1,000,000": { fixed: "$0.0040", tiered: "$0.0020" },
  "1,000,001 to 10,000,000": { fixed: "$0.0040", tiered: "$0.0015" },
  "10,000,001 to 50,000,000": { fixed: "$0.0040", tiered: "$0.0010" },
  "50,000,001 or more": { fixed: "$0.0040", tiered: "$0.0005" },
};
